import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, useCallback } from 'react';
import {
  searchProviders,
  getProviderById,
  buildSearchParams,
  parseSearchParams,
} from '../api/searchApi';
import { SearchFilters } from '../types';
import { apiClient } from '../../../core/api/client';

/**
 * Query key factory for provider search queries
 * 
 * Implements hierarchical query keys for granular cache invalidation:
 * - all: invalidate all provider searches
 * - list: invalidate all search queries
 * - detail: invalidate specific provider details
 */
export const providerSearchKeys = {
  all: ['providerSearch'] as const,
  lists: () => [...providerSearchKeys.all, 'list'] as const,
  list: (filters: SearchFilters) =>
    [...providerSearchKeys.lists(), filters] as const,
  details: () => [...providerSearchKeys.all, 'detail'] as const,
  detail: (id: string) => [...providerSearchKeys.details(), id] as const,
};

/**
 * Default search filters
 */
const getDefaultFilters = (): SearchFilters => ({
  providerType: 'SERVICE_PROVIDER',
  page: 1,
  limit: 20,
  categoryIds: [],
  specialtyIds: [],
  expertiseIds: [],
  serviceRanges: [],
});

/**
 * Get localStorage key for filter persistence
 * Uses user-specific key to support multiple accounts per browser
 * 
 * @param userId - Current user ID (from auth token)
 * @returns Storage key string or null if userId is not available
 */
const getStorageKey = (userId: string | undefined): string | null => {
  return userId ? `bquest:filters:${userId}:provider-directory` : null;
};

/**
 * Load filters from localStorage
 * 
 * @param userId - Current user ID
 * @returns Saved filters or defaults
 */
const loadFiltersFromStorage = (
  userId: string | undefined
): SearchFilters => {
  try {
    const key = getStorageKey(userId);
    if (!key) {
      // No userId yet (auth still loading), return defaults without accessing localStorage
      return getDefaultFilters();
    }
    const saved = localStorage.getItem(key);
    if (saved) {
      const parsed = JSON.parse(saved);
      return { ...getDefaultFilters(), ...parsed };
    }
  } catch (error) {
    console.error('Failed to load search filters from localStorage:', error);
  }
  return getDefaultFilters();
};

/**
 * Save filters to localStorage
 * 
 * @param userId - Current user ID
 * @param filters - Filters to save
 */
const saveFiltersToStorage = (
  userId: string | undefined,
  filters: SearchFilters
): void => {
  try {
    const key = getStorageKey(userId);
    if (!key) {
      // No userId yet (auth still loading), skip saving to prevent using guest key
      return;
    }
    localStorage.setItem(key, JSON.stringify(filters));
  } catch (error) {
    console.error('Failed to save search filters to localStorage:', error);
  }
};

/**
 * Hook for provider search with filter persistence
 * 
 * Implements FR-018 through FR-029:
 * - Multi-select taxonomy filtering
 * - Geographic search with radius
 * - Service range filtering
 * - Pagination
 * - Filter persistence via localStorage
 * 
 * @param userId - Current user ID for filter persistence
 * @param initialFilters - Optional initial filters (overrides localStorage)
 * @param options.skipStoredFilters - Start from defaults instead of the saved
 *   filters (e.g. a link that sets its own filters). Changes are still saved.
 * @returns Search state and filter management functions
 */
export const useProviderSearch = (
  userId: string | undefined,
  initialFilters?: Partial<SearchFilters>,
  isAuthLoading: boolean = false,
  { skipStoredFilters = false }: { skipStoredFilters?: boolean } = {}
) => {
  // Track when filters are ready to fire the query.
  // - Auth users with userId already in hand: ready immediately (persisted filters
  //   loaded via initial state below; uat behavior).
  // - Guest users (Item 1 pre-login): ready once auth finishes loading and we know
  //   there's no userId — no localStorage persistence to wait for.
  // - Auth still resolving + no userId yet: wait.
  const [hasLoadedUserFilters, setHasLoadedUserFilters] = useState(
    () => !!userId || !isAuthLoading,
  );

  const [filters, setFilters] = useState<SearchFilters>(() => {
    const stored = skipStoredFilters ? getDefaultFilters() : loadFiltersFromStorage(userId);
    return initialFilters ? { ...stored, ...initialFilters } : stored;
  });

  useEffect(() => {
    if (userId && !hasLoadedUserFilters) {
      const stored = skipStoredFilters ? getDefaultFilters() : loadFiltersFromStorage(userId);
      setFilters(initialFilters ? { ...stored, ...initialFilters } : stored);
      setHasLoadedUserFilters(true);
    }
  }, [userId, hasLoadedUserFilters, initialFilters, skipStoredFilters]);

  // Once auth finishes loading and there's no userId, mark guest as ready.
  useEffect(() => {
    if (!isAuthLoading && !userId && !hasLoadedUserFilters) {
      setHasLoadedUserFilters(true);
    }
  }, [isAuthLoading, userId, hasLoadedUserFilters]);

  // Persist filters to localStorage on change (only when userId is available)
  useEffect(() => {
    if (userId && hasLoadedUserFilters) {
      saveFiltersToStorage(userId, filters);
    }
  }, [userId, filters, hasLoadedUserFilters]);

  // Gate the query on hasLoadedUserFilters to avoid a phantom fetch with
  // default filters before auth resolves and the persisted filters are loaded.
  const query = useQuery({
    queryKey: providerSearchKeys.list(filters),
    queryFn: () => searchProviders(filters),
    staleTime: 2 * 60 * 1000,
    enabled: hasLoadedUserFilters,
  });

  /**
   * Update filters (partial update)
   */
  const updateFilters = useCallback((updates: Partial<SearchFilters>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  /**
   * Reset filters to defaults and clear from localStorage
   */
  const resetFilters = useCallback(() => {
    const key = getStorageKey(userId);
    if (key) {
      localStorage.removeItem(key);
    }
    setFilters(getDefaultFilters());
  }, [userId]);

  /**
   * Set specific page
   */
  const setPage = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  /**
   * Clear all filters except pagination
   */
  const clearFilters = useCallback(() => {
    setFilters((prev) => ({
      page: prev.page,
      limit: prev.limit,
    }));
  }, []);

  /**
   * Add category filter
   */
  const addCategoryFilter = useCallback((categoryId: string) => {
    setFilters((prev) => ({
      ...prev,
      categoryIds: [...(prev.categoryIds || []), categoryId],
      page: 1, // Reset to first page when filter changes
    }));
  }, []);

  /**
   * Remove category filter
   */
  const removeCategoryFilter = useCallback((categoryId: string) => {
    setFilters((prev) => ({
      ...prev,
      categoryIds: (prev.categoryIds || []).filter((id) => id !== categoryId),
      page: 1,
    }));
  }, []);

  /**
   * Add specialty filter
   */
  const addSpecialtyFilter = useCallback((specialtyId: string) => {
    setFilters((prev) => ({
      ...prev,
      specialtyIds: [...(prev.specialtyIds || []), specialtyId],
      page: 1,
    }));
  }, []);

  /**
   * Remove specialty filter
   */
  const removeSpecialtyFilter = useCallback((specialtyId: string) => {
    setFilters((prev) => ({
      ...prev,
      specialtyIds: (prev.specialtyIds || []).filter((id) => id !== specialtyId),
      page: 1,
    }));
  }, []);

  /**
   * Add expertise filter
   */
  const addExpertiseFilter = useCallback((expertiseId: string) => {
    setFilters((prev) => ({
      ...prev,
      expertiseIds: [...(prev.expertiseIds || []), expertiseId],
      page: 1,
    }));
  }, []);

  /**
   * Remove expertise filter
   */
  const removeExpertiseFilter = useCallback((expertiseId: string) => {
    setFilters((prev) => ({
      ...prev,
      expertiseIds: (prev.expertiseIds || []).filter((id) => id !== expertiseId),
      page: 1,
    }));
  }, []);

  /**
   * Set location filter
   */
  const setLocationFilter = useCallback(
    (location: {
      latitude: number;
      longitude: number;
      radiusMiles: number;
    }) => {
      setFilters((prev) => ({
        ...prev,
        latitude: location.latitude,
        longitude: location.longitude,
        radiusMiles: location.radiusMiles,
        page: 1,
      }));
    },
    []
  );

  /**
   * Clear location filter
   */
  const clearLocationFilter = useCallback(() => {
    setFilters((prev) => {
      const { latitude, longitude, radiusMiles, ...rest } = prev;
      return { ...rest, page: 1 };
    });
  }, []);

  /**
   * Check if any filters are active (excluding pagination)
   */
  const hasActiveFilters = useCallback((): boolean => {
    const defaults = getDefaultFilters();
    return (
      (filters.categoryIds?.length || 0) > 0 ||
      (filters.specialtyIds?.length || 0) > 0 ||
      (filters.expertiseIds?.length || 0) > 0 ||
      (filters.serviceRanges?.length || 0) > 0 ||
      filters.newClientAcceptance !== defaults.newClientAcceptance ||
      filters.latitude !== undefined ||
      filters.stateCode !== undefined
    );
  }, [filters]);

  return {
    // Query state
    providers: query.data?.providers || [],
    searchContextId: query.data?.searchContextId,
    total: query.data?.total || 0,
    page: query.data?.page || filters.page || 1,
    limit: query.data?.limit || filters.limit || 20,
    totalPages: query.data?.totalPages || 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isFetching: query.isFetching,

    // Current filters
    filters,

    // Filter management
    updateFilters,
    resetFilters,
    clearFilters,
    setPage,
    addCategoryFilter,
    removeCategoryFilter,
    addSpecialtyFilter,
    removeSpecialtyFilter,
    addExpertiseFilter,
    removeExpertiseFilter,
    setLocationFilter,
    clearLocationFilter,
    hasActiveFilters: hasActiveFilters(),

    // Utility functions
    buildSearchParams: () => buildSearchParams(filters),
    parseSearchParams,
  };
};

/**
 * Hook to fetch a specific provider by ID
 * 
 * Used for provider detail pages and modals.
 * Only APPROVED providers with ACTIVE accounts are accessible (FR-121).
 * 
 * @param providerId - Provider ID to fetch
 * @returns Provider details query
 */
export const useProviderById = (providerId: string | undefined) => {
  return useQuery({
    queryKey: providerSearchKeys.detail(providerId || ''),
    queryFn: () => getProviderById(providerId!),
    enabled: !!providerId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to prefetch provider details for optimistic navigation
 * 
 * Call this when hovering over a provider card to preload details
 * 
 * @returns Prefetch function
 */
export const usePrefetchProvider = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (providerId: string) => {
      queryClient.prefetchQuery({
        queryKey: providerSearchKeys.detail(providerId),
        queryFn: () => getProviderById(providerId),
        staleTime: 5 * 60 * 1000,
      });
    },
    [queryClient]
  );
};

/**
 * Hook to fetch base provider profile (hero section only - lazy loading)
 * 
 * @param providerId - Provider ID to fetch
 * @param enabled - Whether to enable the query (default: true)
 * @returns Base provider profile query
 */
export const useProviderBase = (providerId: string | undefined, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['provider', 'base', providerId],
    queryFn: async () => {
      const response = await apiClient.get(`/providers/${providerId}/base`);
      return response.data;
    },
    enabled: !!providerId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch provider services (parallel loading on page open)
 * 
 * @param providerId - Provider ID to fetch services for
 * @param enabled - Whether to enable the query (default: true, load immediately)
 * @returns Provider services query
 */
export const useProviderServices = (providerId: string | undefined, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['provider', 'services', providerId],
    queryFn: async () => {
      const response = await apiClient.get(`/providers/${providerId}/services`);
      return response.data;
    },
    enabled: !!providerId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch provider qualifications (parallel loading on page open)
 * 
 * @param providerId - Provider ID to fetch qualifications for
 * @param enabled - Whether to enable the query (default: true, load immediately)
 * @returns Provider qualifications query (badges, expertise, specialties)
 */
export const useProviderQualifications = (providerId: string | undefined, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['provider', 'qualifications', providerId],
    queryFn: async () => {
      const response = await apiClient.get(`/providers/${providerId}/qualifications`);
      return response.data;
    },
    enabled: !!providerId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch provider media/photos (parallel loading on page open)
 * 
 * @param providerId - Provider ID to fetch media for
 * @param enabled - Whether to enable the query (default: true, load immediately)
 * @returns Provider media query
 */
export const useProviderMedia = (providerId: string | undefined, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['provider', 'media', providerId],
    queryFn: async () => {
      const response = await apiClient.get(`/providers/${providerId}/media`);
      return response.data;
    },
    enabled: !!providerId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch lightweight provider reviews summary (just rating and count)
 * Optimized for performance - does not fetch full review objects
 * 
 * @param providerId - Provider ID to fetch reviews summary for
 * @param enabled - Whether to enable the query (default: true, load immediately)
 * @returns Provider reviews summary query (averageRating and totalReviewCount only)
 */
export const useProviderReviewsSummary = (providerId: string | undefined, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['provider', 'reviews', 'summary', providerId],
    queryFn: async () => {
      try {
        const response = await apiClient.get(`/providers/${providerId}/reviews/summary`);
        return response.data;
      } catch (error: any) {
        // Gracefully handle errors - return empty summary
        return {
          averageRating: null,
          totalReviewCount: 0,
        };
      }
    },
    enabled: !!providerId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes (longer cache since rating summary changes less frequently)
    retry: 1, // Only retry once for lightweight calls
  });
};

/**
 * Hook to fetch provider Google reviews (parallel loading on page open)
 * 
 * @param providerId - Provider ID to fetch reviews for
 * @param enabled - Whether to enable the query (default: true, load immediately)
 * @returns Provider reviews query (from Google Places API)
 */
export const useProviderReviews = (providerId: string | undefined, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['provider', 'reviews', providerId],
    queryFn: async () => {
      try {
        const response = await apiClient.get(`/providers/${providerId}/reviews`);
        return response.data;
      } catch (error: any) {
        if (error.statusCode === 503) {
          throw new Error('Google Places API unavailable. Please try again later.');
        }
        throw new Error('Failed to fetch provider reviews');
      }
    },
    enabled: !!providerId && enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes (shorter cache for real-time reviews)
    retry: (failureCount, error: any) => {
      // Don't retry if Google API is unavailable (503)
      if (error.message?.includes('Google Places API unavailable')) {
        return false;
      }
      return failureCount < 2;
    },
  });
};
